import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Box
} from '@mui/material';
import { useSnackbar } from 'notistack';
import AppService from '../../Configurations/Appservice';

/**
 * Dialog component for changing a ticket's status.
 * Supports mandatory comments for certain status types.
 */
const StatusChangeDialog = ({ open, onClose, ticket, onStatusChanged }) => {
  const [newStatus, setNewStatus] = useState('');
  const [comments, setComments] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Define which statuses require comments
  const commentsRequiredStatuses = ['REJECTED', 'INCOMPLETE', 'MODIFICATION_REQUIRED'];
  
  // Available status options based on the current ticket status
  const getAvailableStatuses = () => {
    // Only use valid statuses from the backend TicketStatus enum
    const statuses = [
      // Valid statuses from backend TicketStatus enum
      { value: 'OPEN', label: 'Open' },
      { value: 'TAKEN', label: 'Taken' },
      { value: 'ASSIGNED', label: 'Assigned' },
      { value: 'IN_PROGRESS', label: 'In Progress' }, // Will work after backend is updated
      { value: 'COMPLETED', label: 'Completed' },
      { value: 'CLOSED', label: 'Closed' },
      { value: 'REJECTED', label: 'Rejected' },
      { value: 'INCOMPLETE', label: 'Incomplete' },
      { value: 'MODIFICATION_REQUIRED', label: 'Modification Required' }
    ];
    
    // Filter out the current status - can't change to same status
    return statuses.filter(status => status.value !== ticket?.status);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!newStatus) {
      newErrors.status = 'Please select a status';
    }
    
    if (commentsRequiredStatuses.includes(newStatus) && (!comments || comments.trim() === '')) {
      newErrors.comments = `Comments are required when changing status to ${newStatus.replace('_', ' ').toLowerCase()}`;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Make API call to update status using AppService with the correct endpoint
      console.log('Sending status change request:', {
        ticketId: ticket.id,
        newStatus,
        comments,
        reassign: newStatus === 'MODIFICATION_REQUIRED'
      });
      
      await AppService.post(
        '/api/tickets/status/change', 
        {
          ticketId: ticket.id,
          newStatus,
          comments,
          reassign: newStatus === 'MODIFICATION_REQUIRED'
        }
      );
      
      enqueueSnackbar('Ticket status updated successfully', { variant: 'success' });
      setLoading(false);
      onStatusChanged(newStatus);
      handleClose();
    } catch (error) {
      console.error('Error updating ticket status:', error);
      
      // Extract error data from response
      const errorData = error.response?.data;
      console.log('Error response data:', errorData);
      
      // Get the error message and code from the structured response
      let errorMessage = 'Failed to update ticket status';
      let errorCode = 'UNKNOWN_ERROR';
      
      // Handle different response formats
      if (errorData) {
        if (typeof errorData === 'string') {
          // Handle string responses (legacy format)
          errorMessage = errorData;
        } else if (errorData.message) {
          // Handle new structured error format with message and code
          errorMessage = errorData.message;
          errorCode = errorData.code || 'UNKNOWN_ERROR';
          console.log(`Error code: ${errorCode}, message: ${errorMessage}`);
        }
      } else if (error.message && error.message !== 'Request failed with status code 403') {
        errorMessage = error.message;
      }
      
      // Show error in a snackbar
      enqueueSnackbar(errorMessage, { variant: 'error' });
      
      // For permission denied errors or workflow-related errors, show a more prominent notification
      if (errorCode === 'PERMISSION_DENIED' || 
          errorMessage.includes('workflow') || 
          errorMessage.includes('permission')) {
        // Use MUI alert dialog through the snackbar with higher prominence
        enqueueSnackbar(errorMessage, { 
          variant: 'error',
          preventDuplicate: true,
          autoHideDuration: 8000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          }
        });
      }
      
      setLoading(false);
    }
  };

  const handleClose = () => {
    setNewStatus('');
    setComments('');
    setErrors({});
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ pb: 1 }}>
        Change Ticket Status
      </DialogTitle>
      
      <DialogContent sx={{ pb: 0.5, pt: 1 }}>
        <Box sx={{ mb: 2, mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Current Status:
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 500 }}>
            {ticket?.status ? ticket.status.replace(/_/g, ' ') : 'Unknown'}
          </Typography>
        </Box>
        
        <FormControl fullWidth sx={{ mb: 2 }} error={!!errors.status}>
          <InputLabel id="new-status-label">New Status</InputLabel>
          <Select
            labelId="new-status-label"
            value={newStatus}
            label="New Status"
            onChange={(e) => setNewStatus(e.target.value)}
          >
            {getAvailableStatuses().map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </Select>
          {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
        </FormControl>
        
        <FormControl fullWidth error={!!errors.comments}>
          <TextField
            label="Comments"
            multiline
            rows={4}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Add comments about this status change"
            error={!!errors.comments}
            helperText={errors.comments}
          />
        </FormControl>
      </DialogContent>
      
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} color="inherit" disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="primary" 
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update Status'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StatusChangeDialog;
